import Service, { inject as service } from '@ember/service';
import { dropTask } from 'ember-concurrency';
import DomainModel from 'cio/models/domain';
import ApiService, { ApiError } from 'cio/services/api';
import ApiErrorsService from 'cio/services/api-errors';
import AnalyticsService from 'cio/services/analytics';
import { EventsType } from 'cio/utils/analytics/events';

function filterErrorByType(errors: ApiError[], types: string[]): ApiError[] {
  let pointers = types.map((type) => `/data/attributes/${type}`);
  return errors.filter((error: ApiError) => error.source && pointers.includes(error.source.pointer));
}

type DomainVerificationSource = 'verify_authentication' | 'link_tracking';

const DomainVerificationEventName: Record<DomainVerificationSource, keyof EventsType['email']> = {
  verify_authentication: 'deliverability_domain_checking_started',
  link_tracking: 'link_tracking_domain_checking_started',
};

export default class DomainVerificationService extends Service {
  @service api!: ApiService;
  @service apiErrors!: ApiErrorsService;
  @service analytics!: AnalyticsService;

  verifyTask = dropTask(async (domain: DomainModel, source: DomainVerificationSource) => {
    let { id, cnameOrFallback } = domain;
    this.analytics.track('email', DomainVerificationEventName[source]);

    try {
      let url = `domains/${id}`;
      await this.api.put(url, { data: { cname: cnameOrFallback }, ignoreErrors: true });
    } catch (response) {
      let { errors } = response;
      if (!errors || errors[0].status !== '422') {
        return this.apiErrors.report(response);
      }

      let linkDomainErrors = filterErrorByType(errors, ['link domain', 'link_domain']).map((error) => error.detail);

      if (linkDomainErrors.length > 0) {
        throw linkDomainErrors;
      }
    } finally {
      await domain.reload();
    }
  });
}

declare module '@ember/service' {
  interface Registry {
    'domain-verification': DomainVerificationService;
  }
}
